import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import "./LeaveType.scss";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import GeneralTable from "../../../components/Table/GeneralTable";
import EmployeeLeaveBalancePopup from "./EmployeeLeaveBalancePopup";
import { toast } from "react-toastify";

function LeaveType({ markPageAsCompleted }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: "",
    days: "",
    noOfYears: "",
    payroll: "paid",
    carryForward: false,
    carryForwardDays: 0,
    encashment: false,
    encashmentDays: 0,
    optionalHoliday: false,
  });
  const [searchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdate, setBulkUpdate] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    leaveTypeId: null,
    leaveTypeName: "",
    isBulkDelete: false,
  });
  const [showLeaveBalancePopup, setShowLeaveBalancePopup] = useState(false);

  // FIX: Wrap fetchLeaveTypes in useCallback
  const fetchLeaveTypes = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/leave-types/all`,
      );

      if (Array.isArray(response.data?.data) && response.data.data.length > 0) {
        const mappedLeaveTypes = response.data.data.map((leaveType) => ({
          id: leaveType.leaveTypeId,
          name: leaveType.leaveType,
          days: leaveType.periodNoDays,
          noOfYears: leaveType.noOfYears || 0,
          payroll: leaveType.payrollType
            ? leaveType.payrollType.toLowerCase()
            : "paid",
          carryForward: leaveType.carryForward,
          carryForwardDays: leaveType.carryForwardDays || 0,
          encashment: leaveType.enchasment,
          encashmentDays: leaveType.enchasmentDays || 0,
          optionalHoliday: leaveType.optionalHoliday || false,
          status: leaveType.status,
        }));

        setLeaveTypes(mappedLeaveTypes);
        markPageAsCompleted("Leave Type");
      } else {
        console.error(
          "Unexpected data format or empty leave types:",
          response.data,
        );
      }
    } catch (error) {
      console.error("Error fetching leave types:", error);
      setError("Failed to fetch leave types. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [markPageAsCompleted]); // Dependency for useCallback

  // FIX: Add fetchLeaveTypes to the dependency array
  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  const filteredLeaveTypes = leaveTypes.filter(
    (leaveType) =>
      leaveType.name &&
      leaveType.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const displayedLeaveTypes = filteredLeaveTypes;

  useEffect(() => {
    const displayedIds = displayedLeaveTypes.map((lt) => lt.id);
    const allDisplayedSelected =
      displayedIds.length > 0 &&
      displayedIds.every((id) => selectedLeaveTypes.includes(id));
    setSelectAll(allDisplayedSelected);
  }, [selectedLeaveTypes, displayedLeaveTypes]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      // when carry forward is turned OFF → set days = 0
      if (name === "carryForward") {
        return {
          ...prev,
          carryForward: checked,
          carryForwardDays: checked ? prev.carryForwardDays : 0,
        };
      }

      // when encashment is turned OFF → set days = 0
      if (name === "encashment") {
        return {
          ...prev,
          encashment: checked,
          encashmentDays: checked ? prev.encashmentDays : 0,
        };
      }

      return {
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
              ? Number(value)
              : value,
      };
    });
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setError("Leave Type Name is required.");
      return false;
    }
    if (!form.days || form.days <= 0) {
      setError("Number of Days must be greater than 0.");
      return false;
    }
    if (!form.noOfYears || form.noOfYears <= 0) {
      setError("Number of Years must be greater than 0.");
      return false;
    }
    if (
      form.carryForward &&
      (!form.carryForwardDays || form.carryForwardDays <= 0)
    ) {
      setError(
        "Carry Forward Days must be greater than 0 when Carry Forward is enabled.",
      );
      return false;
    }
    if (form.encashment && (!form.encashmentDays || form.encashmentDays <= 0)) {
      setError(
        "Encashment Days must be greater than 0 when Encashment is enabled.",
      );
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const formData = {
      leaveTypeId: form.id || 0,
      leaveType: form.name.trim(),
      periodNoDays: parseInt(form.days),
      noOfYears: parseInt(form.noOfYears),
      payrollType: form.payroll.toUpperCase(),
      carryForward: form.carryForward,
      carryForwardDays: form.carryForward ? parseInt(form.carryForwardDays) : 0,
      encashment: form.encashment,
      encashmentDays: form.encashment ? parseInt(form.encashmentDays) : 0,
      optionalHoliday: form.optionalHoliday,
      status: "ENABLED",
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
    };

    try {
      if (isEditing) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/leave-types/${form.id}`,
          formData,
        );
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/leave-types`,
          formData,
        );
      }

      toast.success("Leave created successfully");
      await fetchLeaveTypes();
      markPageAsCompleted("Leave Type");
      resetForm();
      setShowForm(false);
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError("Bad request. Please check your input.");
            break;
          case 401:
            setError("Unauthorized. Please log in.");
            break;
          case 403:
            setError(
              "Forbidden. You don't have permission to perform this action.",
            );
            break;
          case 404:
            setError("Leave type not found.");
            break;
          case 500:
            setError("Internal server error. Please try again later.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else {
        setError(
          "Network error. Please check your internet connection and try again.",
        );
      }
      console.error("Error submitting leave type:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (leaveType) => {
    setForm({
      ...leaveType,
      carryForwardDays: leaveType.carryForward ? leaveType.carryForwardDays : 0,
      encashmentDays: leaveType.encashment ? leaveType.encashmentDays : 0,
    });

    setIsEditing(true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm({
      id: null,
      name: "",
      days: "",
      noOfYears: "",
      payroll: "paid",
      carryForward: false,
      carryForwardDays: 0,
      encashment: false,
      encashmentDays: 0,
      optionalHoliday: false,
    });
    setIsEditing(false);
    setError("");
  };

  const handleDeleteClick = async (leaveTypeId, leaveTypeName) => {
    setLoading(true);
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/leave-types/${leaveTypeId}`,
      );
      setLeaveTypes((prevLeaveTypes) =>
        prevLeaveTypes.filter((leaveType) => leaveType.id !== leaveTypeId),
      );
      toast.success(`Leave type "${leaveTypeName}" deleted successfully.`);
    } catch (error) {
      console.error("Error deleting leave type:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedLeaveTypes.length === 0) {
      setError("Please select at least one leave type to delete.");
      return;
    }

    setWarningPopup({
      isOpen: true,
      leaveTypeId: null,
      leaveTypeName: "",
      isBulkDelete: true,
    });
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      if (warningPopup.isBulkDelete) {
        await axiosInstance.delete(
          `${process.env.REACT_APP_API_BASE_URL}/leave-types`,
          {
            data: selectedLeaveTypes,
          },
        );
        setLeaveTypes((prevLeaveTypes) =>
          prevLeaveTypes.filter(
            (leaveType) => !selectedLeaveTypes.includes(leaveType.id),
          ),
        );
        setSelectedLeaveTypes([]);
        setSelectAll(false);
      } else {
        await axiosInstance.delete(
          `${process.env.REACT_APP_API_BASE_URL}/leave-types/${warningPopup.leaveTypeId}`,
        );
        setLeaveTypes((prevLeaveTypes) =>
          prevLeaveTypes.filter(
            (leaveType) => leaveType.id !== warningPopup.leaveTypeId,
          ),
        );
      }
    } catch (error) {
      console.error("Error deleting leave type(s):", error);
    } finally {
      setLoading(false);
      setWarningPopup({
        isOpen: false,
        leaveTypeId: null,
        leaveTypeName: "",
        isBulkDelete: false,
      });
    }
  };

  const handleDeleteDecline = () => {
    setWarningPopup({
      isOpen: false,
      leaveTypeId: null,
      leaveTypeName: "",
      isBulkDelete: false,
    });
  };

  const toggleRoleStatus = async (leaveTypeId, currentStatus) => {
    const newStatus = currentStatus === "Enabled" ? "Disabled" : "Enabled";

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/leave-types/${leaveTypeId}/${
          newStatus === "Enabled" ? "enable" : "disable"
        }`,
      );
      setLeaveTypes((prevLeaveTypes) =>
        prevLeaveTypes.map((leaveType) =>
          leaveType.id === leaveTypeId
            ? { ...leaveType, status: newStatus }
            : leaveType,
        ),
      );
    } catch (error) {
      console.error("Error toggling leave type status:", error);
      setError("Failed to update status. Please try again.");
    }
  };

  const handleBulkUpdateChange = (id, field, value) => {
    setLeaveTypes((prevLeaveTypes) =>
      prevLeaveTypes.map((leaveType) =>
        leaveType.id === id ? { ...leaveType, [field]: value } : leaveType,
      ),
    );
  };

  const handleBulkUpdateSubmit = async () => {
    setLoading(true);

    try {
      await Promise.all(
        leaveTypes.map((leaveType) => {
          const formData = {
            leaveTypeId: leaveType.id,
            leaveType: leaveType.name,
            periodNoDays: parseInt(leaveType.days),
            noOfYears: parseInt(leaveType.noOfYears),
            payrollType: leaveType.payroll.toUpperCase(),
            carryForward: leaveType.carryForward,
            carryForwardDays: leaveType.carryForward
              ? parseInt(leaveType.carryForwardDays)
              : 0,
            encashment: leaveType.encashment,
            encashmentDays: leaveType.encashment
              ? parseInt(leaveType.encashmentDays)
              : 0,
            optionalHoliday: leaveType.optionalHoliday,
            status: "ENABLED",
            companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
          };

          return axiosInstance.put(
            `${process.env.REACT_APP_API_BASE_URL}/leave-types/${leaveType.id}`,
            formData,
          );
        }),
      );
      toast.success("Bulk update submitted successfully");
      await fetchLeaveTypes();
      setBulkUpdate(false);
      markPageAsCompleted("Leave Type");
    } catch (error) {
      console.error("Error during bulk update:", error);
      setError("Bulk update failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const displayedIds = displayedLeaveTypes.map((lt) => lt.id);
    if (selectAll) {
      setSelectedLeaveTypes((prev) =>
        prev.filter((id) => !displayedIds.includes(id)),
      );
    } else {
      setSelectedLeaveTypes((prev) => {
        const newSelection = [...prev];
        displayedIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleIndividualSelect = (leaveTypeId) => {
    setSelectedLeaveTypes((prev) => {
      if (prev.includes(leaveTypeId)) {
        return prev.filter((id) => id !== leaveTypeId);
      } else {
        return [...prev, leaveTypeId];
      }
    });
  };

  const getTableColumns = () => {
    const baseColumns = [
      {
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ColoredCheckbox
              name="select-all"
              color="green"
              checked={selectAll}
              onChange={handleSelectAll}
              value="all"
            />
            <span>Leave Type</span>
          </div>
        ),
        accessor: "name",
      },
      { header: "Days", accessor: "days" },
      { header: "Years", accessor: "noOfYears" },
      { header: "Payroll", accessor: "payroll" },
      { header: "Carry Forward", accessor: "carryForward" },
      { header: "Encashment", accessor: "encashment" },
      { header: "Optional Holiday", accessor: "optionalHoliday" },
      { header: "Status", accessor: "status" },
      { header: "Actions", accessor: "actions" },
      { header: "", accessor: "searchableText", hidden: true },
    ];

    return baseColumns;
  };

  const getFormattedTableData = () => {
    return displayedLeaveTypes.map((leaveType) => {
      const isEnabled = leaveType.status === "Enabled";
      const isSelected = selectedLeaveTypes.includes(leaveType.id);

      return {
        name: (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <ColoredCheckbox
              checked={isSelected}
              color="green"
              onChange={() => handleIndividualSelect(leaveType.id)}
            />
            <span>{leaveType.name}</span>
          </div>
        ),
        days: bulkUpdate ? (
          <input
            type="number"
            className="leave-type__bulk-input"
            value={leaveType.days}
            min="1"
            onChange={(e) =>
              handleBulkUpdateChange(leaveType.id, "days", e.target.value)
            }
          />
        ) : (
          leaveType.days
        ),
        noOfYears: bulkUpdate ? (
          <input
            type="number"
            className="leave-type__bulk-input"
            value={leaveType.noOfYears}
            min="1"
            onChange={(e) =>
              handleBulkUpdateChange(leaveType.id, "noOfYears", e.target.value)
            }
          />
        ) : (
          leaveType.noOfYears
        ),
        payroll: bulkUpdate ? (
          <select
            value={leaveType.payroll}
            className="leave-type__bulk-select"
            onChange={(e) =>
              handleBulkUpdateChange(leaveType.id, "payroll", e.target.value)
            }
          >
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        ) : (
          <span
            className={`leave-type__badge leave-type__badge--${leaveType.payroll}`}
          >
            {leaveType.payroll}
          </span>
        ),
        carryForward: bulkUpdate ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ColoredCheckbox
              checked={leaveType.carryForward}
              color="green"
              onChange={() => {
                const newValue = !leaveType.carryForward;

                // Update checkbox
                handleBulkUpdateChange(leaveType.id, "carryForward", newValue);

                // Reset days if disabled
                if (!newValue) {
                  handleBulkUpdateChange(leaveType.id, "carryForwardDays", 0);
                }
              }}
            />

            {leaveType.carryForward && (
              <input
                type="number"
                className="leave-type__bulk-input"
                style={{ width: "60px" }}
                value={leaveType.carryForwardDays}
                min="1"
                placeholder="Days"
                onChange={(e) =>
                  handleBulkUpdateChange(
                    leaveType.id,
                    "carryForwardDays",
                    Number(e.target.value),
                  )
                }
              />
            )}
          </div>
        ) : (
          <span
            className={`leave-type__status ${
              leaveType.carryForward
                ? "leave-type__status--yes"
                : "leave-type__status--no"
            }`}
          >
            {leaveType.carryForward
              ? `Yes (${leaveType.carryForwardDays} days)`
              : "No"}
          </span>
        ),

        encashment: bulkUpdate ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ColoredCheckbox
              checked={leaveType.encashment}
              color="green"
              onChange={() => {
                const newValue = !leaveType.encashment;

                // Update checkbox
                handleBulkUpdateChange(leaveType.id, "encashment", newValue);

                // Reset days if disabled
                if (!newValue) {
                  handleBulkUpdateChange(leaveType.id, "encashmentDays", 0);
                }
              }}
            />

            {leaveType.encashment && (
              <input
                type="number"
                className="leave-type__bulk-input"
                style={{ width: "60px" }}
                value={leaveType.encashmentDays}
                min="1"
                placeholder="Days"
                onChange={(e) =>
                  handleBulkUpdateChange(
                    leaveType.id,
                    "encashmentDays",
                    Number(e.target.value),
                  )
                }
              />
            )}
          </div>
        ) : (
          <span
            className={`leave-type__status ${
              leaveType.encashment
                ? "leave-type__status--yes"
                : "leave-type__status--no"
            }`}
          >
            {leaveType.encashment
              ? `Yes (${leaveType.encashmentDays} days)`
              : "No"}
          </span>
        ),
        optionalHoliday: bulkUpdate ? (
          <ColoredCheckbox
            checked={leaveType.optionalHoliday}
            color="blue"
            onChange={() =>
              handleBulkUpdateChange(
                leaveType.id,
                "optionalHoliday",
                !leaveType.optionalHoliday,
              )
            }
          />
        ) : (
          <span
            className={`leave-type__status ${
              leaveType.optionalHoliday
                ? "leave-type__status--yes"
                : "leave-type__status--no"
            }`}
          >
            {leaveType.optionalHoliday ? "Yes" : "No"}
          </span>
        ),
        status: (
          <ToggleButton
            isOn={isEnabled}
            onToggle={() => toggleRoleStatus(leaveType.id, leaveType.status)}
            status={!isEnabled}
          />
        ),
        actions: (
          <div className="action-buttons">
            <IconButton
              onClick={() => handleEdit(leaveType)}
              icon="iconamoon:edit-light"
              disabled={loading}
              className="icon-button--edit"
              title="Edit Leave Type"
            />
            <IconButton
              icon="fluent:delete-28-regular"
              className="icon-button--delete"
              onClick={() => handleDeleteClick(leaveType.id, leaveType.name)}
              disabled={loading}
              title="Delete Leave Type"
            />
          </div>
        ),
        searchableText: `${leaveType.name} ${leaveType.days} ${leaveType.payroll} ${leaveType.status}`,
      };
    });
  };

  return (
    <div className="leave-type">
      <div className="leave-type__header">
        <h1 className="display6">Leave Type Management</h1>
      </div>
      <WarningPopup
        isOpen={warningPopup.isOpen}
        onClose={handleDeleteDecline}
        onAccept={handleDeleteConfirm}
        onDecline={handleDeleteDecline}
        title={
          warningPopup.isBulkDelete ? "Delete Leave Types" : "Delete Leave Type"
        }
        message={
          warningPopup.isBulkDelete
            ? `Are you sure you want to delete ${selectedLeaveTypes.length} selected leave type(s)? This action cannot be undone.`
            : `Are you sure you want to delete "${warningPopup.leaveTypeName}" leave type? This action cannot be undone.`
        }
        acceptText="Delete"
        declineText="Cancel"
      />
      <div className="leave-type__actions">
        <button
          className={`leave-type__action-btn ${
            showForm ? "leave-type__action-btn--close" : ""
          }`}
          onClick={() => {
            setShowForm((prev) => !prev);
            if (showForm) {
              resetForm();
            }
          }}
          disabled={loading}
        >
          {!showForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="leave-type__action-icon"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showForm ? "Close Leave Form" : "Create Leave"}
        </button>
        <button
          className={`leave-type__action-btn ${
            bulkUpdate ? "leave-type__action-btn--bulk" : ""
          }`}
          onClick={() => {
            if (bulkUpdate) {
              handleBulkUpdateSubmit();
            } else {
              setBulkUpdate(true);
            }
          }}
          disabled={loading}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="leave-type__action-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
          {bulkUpdate ? "Submit Changes" : "Bulk Update"}
        </button>
        <ButtonWithIcon
          icon="fluent:delete-28-regular"
          text={`Bulk Delete (${selectedLeaveTypes.length})`}
          onClick={handleBulkDeleteClick}
          className="cancel-button "
          disabled={loading || selectedLeaveTypes.length === 0}
        />

        <ButtonWithIcon
          icon="ic:twotone-manage-accounts"
          text="Manage Employee Leave"
          onClick={() => setShowLeaveBalancePopup(true)}
        />
      </div>
      {showForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <form onSubmit={handleSubmit} className="leave-type__form">
            <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
              {isEditing ? "Edit Leave Type" : "Create New Leave Type"}
            </h3>

            {/* First Row: Leave Type, Days, Years */}
            <div className="leave-type__form-grid">
              <div className="leave-type__form-group">
                <label htmlFor="name" className="leave-type__form-label">
                  Leave Type Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="leave-type__form-input"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="Eg: Sick leave"
                  required
                  disabled={loading}
                />
              </div>
              <div className="leave-type__form-group">
                <label htmlFor="days" className="leave-type__form-label">
                  Number of Days
                </label>
                <input
                  type="number"
                  id="days"
                  name="days"
                  className="leave-type__form-input"
                  value={form.days}
                  onChange={handleInputChange}
                  placeholder="Number of leave(s) assigned"
                  min="1"
                  required
                  disabled={loading}
                />
              </div>
              <div className="leave-type__form-group">
                <label htmlFor="noOfYears" className="leave-type__form-label">
                  Number of Years
                </label>
                <input
                  type="number"
                  id="noOfYears"
                  name="noOfYears"
                  className="leave-type__form-input"
                  value={form.noOfYears}
                  onChange={handleInputChange}
                  placeholder="Period in years"
                  min="1"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Second Row: Payroll Type */}
            <div className="leave-type__form-grid">
              <div className="leave-type__form-group">
                <label className="leave-type__form-label">Payroll Type</label>
                <div className="leave-type__payroll-buttons">
                  <button
                    type="button"
                    className={`leave-type__payroll-btn ${
                      form.payroll === "paid"
                        ? "leave-type__payroll-btn--active"
                        : ""
                    }`}
                    onClick={() =>
                      handleInputChange({
                        target: { name: "payroll", value: "paid" },
                      })
                    }
                    disabled={loading}
                  >
                    Paid
                  </button>
                  <button
                    type="button"
                    className={`leave-type__payroll-btn ${
                      form.payroll === "unpaid"
                        ? "leave-type__payroll-btn--active"
                        : ""
                    }`}
                    onClick={() =>
                      handleInputChange({
                        target: { name: "payroll", value: "unpaid" },
                      })
                    }
                    disabled={loading}
                  >
                    Unpaid
                  </button>
                </div>
              </div>
            </div>

            {/* Checkboxes with conditional inputs */}
            <div className="leave-type__form-checkboxes">
              <div className="leave-type__checkbox-with-input">
                <ColoredCheckbox
                  label="Carry Forward"
                  color="green"
                  checked={form.carryForward}
                  onChange={handleInputChange}
                  name="carryForward"
                  value="carryForward"
                />
                {form.carryForward && (
                  <input
                    type="number"
                    name="carryForwardDays"
                    className="leave-type__conditional-input"
                    value={form.carryForwardDays}
                    onChange={handleInputChange}
                    placeholder="Days"
                    min="1"
                    disabled={loading}
                  />
                )}
              </div>

              <div className="leave-type__checkbox-with-input">
                <ColoredCheckbox
                  label="Encashment"
                  color="green"
                  checked={form.encashment}
                  onChange={handleInputChange}
                  name="encashment"
                  value="encashment"
                />
                {form.encashment && (
                  <input
                    type="number"
                    name="encashmentDays"
                    className="leave-type__conditional-input"
                    value={form.encashmentDays}
                    onChange={handleInputChange}
                    placeholder="Days"
                    min="1"
                    disabled={loading}
                  />
                )}
              </div>

              <ColoredCheckbox
                label="Optional Holiday"
                color="blue"
                checked={form.optionalHoliday}
                onChange={handleInputChange}
                name="optionalHoliday"
                value="optionalHoliday"
              />
            </div>
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}
            <div className="leave-type__form-actions">
              <ButtonWithIcon
                className="submit-button"
                icon="tdesign:send"
                text={isEditing ? "Update Leave" : "Create Leave"}
                disabled={loading}
                onClick={handleSubmit}
              />
            </div>
          </form>
        </div>
      )}
      <div className="leave-type__table-container">
        {loading ? (
          <LoadingScreen />
        ) : displayedLeaveTypes.length > 0 ? (
          <GeneralTable
            columns={getTableColumns()}
            data={getFormattedTableData()}
            title="Leave Types"
            selectedCount={selectedLeaveTypes.length}
            showPagination={true}
            initialPageSize={10}
          />
        ) : (
          <div className="leave-type__empty-state">
            <svg
              className="leave-type__empty-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3>No leave types found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first leave type"}
            </p>
          </div>
        )}
      </div>

      <EmployeeLeaveBalancePopup
        isOpen={showLeaveBalancePopup}
        onClose={() => setShowLeaveBalancePopup(false)}
      />
    </div>
  );
}

export default LeaveType;
